import Download16 from '@carbon/icons-react/es/download/16';
import { FormInstance } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Alert } from 'components/atoms/alert';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { CopyInput } from 'components/atoms/input/copy-input';
import { DownloadOnClick } from 'components/molecules/download-link/download-on-click';
import { ProductiveHeading07, Text01 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';

export enum SamlFormPage {
  SETUP = 1,
  META = 2,
}
const FormItem = styled(Form.Item)`
  padding-bottom: var(--layout-01);
  margin-bottom: var(--layout-01);
`;
const Header = styled.h6`
  ${ProductiveHeading07};
  ${Text01};
  margin: 0;
  padding-bottom: var(--spacing-03);
`;
const MetaHeader = styled(Header)`
  padding: var(--spacing-06) 0 var(--spacing-05);
`;
const HeaderWrapper = styled.div`
  padding-bottom: var(--spacing-05);
`;

const Page1 = () => {
  const translate = useTranslate();

  return (
    <>
      <HeaderWrapper>
        <Header>
          <FormattedMessage id="settings.saml.setup.form.title" />
        </Header>
        <FormattedMessage id="settings.saml.setup.meta.sub-title" />
      </HeaderWrapper>

      <FormItem
        key="setupPage.audienceUrl"
        name={['setupPage', 'issuerUrl']}
        label={<FormattedMessage id="settings.security.saml.drawer.idpId" />}
        rules={[
          {
            required: true,
            message: <FormattedMessage id="input.required" />,
            type: 'string',
          },
        ]}
      >
        <Input
          placeholder="Paste your IDP ID..."
          autoComplete="off"
          autoFocus
        />
      </FormItem>
      <FormItem
        key="setupPage.ssoUrl"
        name={['setupPage', 'ssoUrl']}
        label={<FormattedMessage id="settings.security.saml.drawer.loginUrl" />}
        rules={[
          {
            required: true,
            message: <FormattedMessage id="input.required" />,
            type: 'string',
          },
        ]}
      >
        <Input
          placeholder="Paste your IDP’s login page URL..."
          autoComplete="off"
          autoFocus
        />
      </FormItem>
      <FormItem
        key="setupPage.certificate"
        name={['setupPage', 'certificate']}
        label={
          <FormattedMessage id="settings.security.saml.drawer.certificate" />
        }
        rules={[
          {
            required: true,
            message: <FormattedMessage id="input.required" />,
            type: 'string',
          },
        ]}
      >
        <TextArea
          placeholder={translate('saml.certificate.placeholder')}
          autoComplete="off"
          autoFocus
        />
      </FormItem>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DownloadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: var(--blue-60);
  gap: var(--spacing-02);
`;

const DownloadMeta = (props) => {
  const { value } = props;

  return (
    <DownloadWrapper>
      <DownloadOnClick url={value}>
        <>
          <FormattedMessage id="settings.saml.download.meta" />
          <Download16 />
        </>
      </DownloadOnClick>
    </DownloadWrapper>
  );
};

const StyledFormItem = styled(FormItem)`
  padding-bottom: 0 !important;
`;

const Page2: FC = () => {
  const translate = useTranslate();

  return (
    <Wrapper>
      <Alert
        message={translate('settings.saml.setup.info')}
        type="info"
        showIcon
      />
      <div>
        <MetaHeader>
          <FormattedMessage id="settings.saml.setup.meta.title" />
        </MetaHeader>

        <StyledFormItem name={['metaPage', 'downloadUrl']}>
          <DownloadMeta />
        </StyledFormItem>
      </div>

      <FormItem
        key="metaPage.entityId"
        name={['metaPage', 'entityId']}
        label={
          <FormattedMessage id="settings.security.saml.drawer.metaEntityId" />
        }
      >
        <CopyInput />
      </FormItem>
      <FormItem
        key="metaPage.acsUrl"
        name={['metaPage', 'acsUrl']}
        label={<FormattedMessage id="settings.saml.sp_login_in" />}
      >
        <CopyInput />
      </FormItem>
      <FormItem
        key="metaPage.certificate"
        name={['metaPage', 'certificate']}
        label={<FormattedMessage id="settings.saml.sp_certificate" />}
      >
        <CopyInput component={TextArea} />
      </FormItem>
    </Wrapper>
  );
};

export const SamlForm: FC<{
  form: FormInstance;
  onValueChange?: (changedValues: any, values: unknown) => void;
  formPage: SamlFormPage;
}> = ({ form, onValueChange, formPage }) => {
  return (
    <Form
      layout="vertical"
      form={form}
      name="IdpForm"
      preserve={false}
      onValuesChange={onValueChange}
    >
      {formPage === SamlFormPage.SETUP ? <Page1 /> : <Page2 />}
    </Form>
  );
};
